import React, { useEffect, useState, useRef } from "react";
import { Select, Button } from "antd";
import { Empty } from "antd";
import html2canvas from "html2canvas";
import "./style.css";

const { Option } = Select;

const CurrentFuelLevels = ({ fuelLevels }) => {
  const allOptionValue = "__ALL__";
  const [selectedTanks, setSelectedTanks] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("dark-mode")
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const tankNames = fuelLevels?.map((tank) => tank.vehicle_name) || [];
    setSelectedTanks(tankNames.slice(0, 4));
  }, [fuelLevels]);

  const handleTankChange = (selectedValues) => {
    if (selectedValues.includes(allOptionValue)) {
      const allSelected = selectedTanks.length === fuelLevels.length;
      setSelectedTanks(
        allSelected ? [] : fuelLevels.map((tank) => tank.vehicle_name)
      );
    } else {
      setSelectedTanks(selectedValues);
    }
  };
  const [sortConfig, setSortConfig] = useState({});
  const [sortedTanks, setSortedTanks] = useState(fuelLevels || []);

  const handleSort = (key) => {
    let direction = null;

    if (sortConfig.key === key) {
      if (sortConfig.direction === "desc") {
        direction = "asc";
      } else if (sortConfig.direction === "asc") {
        direction = null;
      } else {
        direction = "desc";
      }
    } else {
      direction = "desc";
    }

    setSortConfig({ key: direction ? key : null, direction });

    if (direction) {
      const sorted = [...fuelLevels].sort((a, b) => {
        if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
        if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
        return 0;
      });
      setSortedTanks(sorted);
    } else {
      setSortedTanks(fuelLevels);
    }
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("dark-mode"));
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);
  const handleDownload = async () => {
    if (!containerRef.current) return;

    const elementsToHide =
      containerRef.current.querySelectorAll(".hide-on-export");
    const originalDisplay = [];
    elementsToHide.forEach((el, i) => {
      originalDisplay[i] = el.style.display;
      el.style.display = "none";
    });

    const canvas = await html2canvas(containerRef.current, {
      useCORS: true,
      backgroundColor: null,
      scale: 2,
    });

    elementsToHide.forEach((el, i) => {
      el.style.display = originalDisplay[i];
    });

    const link = document.createElement("a");
    link.download = "current_fuel_levels.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };
  const capitalizeFirst = (str) => {
    if (!str) return "";
    return str
      .split("_")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");
  };
  const FuelLevel = ({ tankLabel, currentLevel, totalCapacity }) => {
    const percentage = (currentLevel / totalCapacity) * 100;

    let fillGradient;
    let borderColor;

    if (percentage >= 50) {
      fillGradient = "linear-gradient(to top, #00de4c, #04b072)";
      borderColor = "#00de4c";
    } else if (percentage >= 21) {
      fillGradient = "linear-gradient(to top, #FFA500, #FFC04D)";
      borderColor = "#FFA500";
    } else {
      fillGradient = "linear-gradient(to top, #FB3748, #FF6B6B)";
      borderColor = "#FB3748";
    }

    return (
      <div className="fuel-tank">
        <div className="fuel-info">Total Cap: {totalCapacity}L</div>
        <div className="fuel-bar">
          <div className="fuel-empty">
            <div
              className="fuel-fill"
              style={{
                height: `${Math.max(percentage, 10)}%`,
                background: fillGradient,
                border: `1px solid ${borderColor}`,
              }}
            >
              <div className="fuel-text">
                <span className="perc">{percentage.toFixed(0)}%</span>
                <br />
                {currentLevel} L
              </div>
            </div>
          </div>
        </div>
        <div className="tank-number">{tankLabel}</div>
      </div>
    );
  };

  return (
    <div className="fuel-levels-container" ref={containerRef}>
      <div className="graph-header">
        <div className="graph-title">Current Fuel Levels</div>
      </div>

      <div className="actions">
        <div className="select-menu">
          <Select
            popupClassName="custom-select-dropdown"
            className="hide-on-export"
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Select Vehicle/s to Display..."
            value={selectedTanks}
            onChange={handleTankChange}
            maxTagCount={0}
          >
            <Option value={allOptionValue}>
              {selectedTanks.length === fuelLevels.length
                ? "Unselect All"
                : "Select All"}
            </Option>
            {fuelLevels?.map((tank) => (
              <Option key={tank.vehicle_name} value={tank.vehicle_name}>
                {tank.vehicle_name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="buttons right hide-on-export">
          <Button
            className="sort-volume"
            onClick={() => handleSort("current_fuel")}
          >
            {capitalizeFirst("current_fuel")} {getSortIcon("current_fuel")}
          </Button>

          <Button onClick={() => handleSort("total_capacity")}>
            {capitalizeFirst("total_capacity")} {getSortIcon("total_capacity")}
          </Button>

          <div className="right hide-on-export">
            <Button onClick={handleDownload}>
              {isDarkMode ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.0149 6.95383L15.9443 7.44883L16.0149 6.95383ZM12.5513 3.83663L12.0589 3.92329V3.92329L12.5513 3.83663ZM4.21756 16.7838L4.4912 16.3653L4.21756 16.7838ZM3.29576 15.9542L3.68481 15.6401L3.29576 15.9542ZM16.7042 15.9542L16.3152 15.6401L16.7042 15.9542ZM15.7824 16.7838L15.5088 16.3653L15.7824 16.7838ZM15.7824 3.21619L16.0561 2.79771L15.7824 3.21619ZM16.7042 4.04581L17.0933 3.73174L16.7042 4.04581ZM4.21756 3.21619L4.4912 3.63466L4.21756 3.21619ZM3.29576 4.04581L3.68481 4.35987L3.29576 4.04581ZM12.5 2.5217L12.5154 2.02193L12.5 2.5217ZM17.4759 7L17.9755 6.98098L17.4759 7ZM10.5 7.75C10.5 7.47386 10.2761 7.25 10 7.25C9.72386 7.25 9.5 7.47386 9.5 7.75H10.5ZM13.6695 11.7202C13.8739 11.5345 13.8891 11.2183 13.7035 11.0139C13.5179 10.8094 13.2016 10.7942 12.9972 10.9798L13.6695 11.7202ZM12.1685 12.4078L12.5046 12.7779L12.1685 12.4078ZM7.83154 12.4078L8.16767 12.0376H8.16767L7.83154 12.4078ZM7.00279 10.9798C6.79836 10.7942 6.48215 10.8094 6.29651 11.0139C6.11087 11.2183 6.1261 11.5345 6.33054 11.7202L7.00279 10.9798ZM10.2089 13.7381L10.2659 14.2348H10.2659L10.2089 13.7381ZM9.79111 13.7381L9.73413 14.2348H9.73413L9.79111 13.7381ZM9.5 13.7381C9.5 14.0142 9.72386 14.2381 10 14.2381C10.2761 14.2381 10.5 14.0142 10.5 13.7381H9.5ZM16.0149 6.95383L16.0854 6.45884C14.4864 6.2309 13.2836 5.11284 13.0437 3.74997L12.5513 3.83663L12.0589 3.92329C12.3838 5.76961 13.9776 7.16848 15.9443 7.44883L16.0149 6.95383ZM17.5 9.25H17V10.75H17.5H18V9.25H17.5ZM2.5 10.75H3V9.25H2.5H2V10.75H2.5ZM10 17.5V17C8.42757 17 7.28443 16.9994 6.39624 16.9128C5.51464 16.8269 4.94215 16.6602 4.4912 16.3653L4.21756 16.7838L3.94392 17.2023C4.58824 17.6236 5.34458 17.815 6.29919 17.9081C7.24721 18.0006 8.44765 18 10 18V17.5ZM2.5 10.75H2C2 12.1438 1.99916 13.2337 2.10305 14.0968C2.2085 14.9727 2.42735 15.6745 2.90671 16.2683L3.29576 15.9542L3.68481 15.6401C3.36841 15.2482 3.18938 14.754 3.09589 13.9773C3.00084 13.1878 3 12.1685 3 10.75H2.5ZM4.21756 16.7838L4.4912 16.3653C4.1797 16.1617 3.90795 15.9165 3.68481 15.6401L3.29576 15.9542L2.90671 16.2683C3.19757 16.6286 3.54797 16.9434 3.94392 17.2023L4.21756 16.7838ZM17.5 10.75H17C17 12.1685 16.9992 13.1878 16.9041 13.9773C16.8106 14.754 16.6316 15.2482 16.3152 15.6401L16.7042 15.9542L17.0933 16.2683C17.5727 15.6745 17.7915 14.9727 17.8969 14.0968C18.0008 13.2337 18 12.1438 18 10.75H17.5ZM10 17.5V18C11.5523 18 12.7528 18.0006 13.7008 17.9081C14.6554 17.815 15.4118 17.6236 16.0561 17.2023L15.7824 16.7838L15.5088 16.3653C15.0578 16.6602 14.4854 16.8269 13.6038 16.9128C12.7156 16.9994 11.5724 17 10 17V17.5ZM16.7042 15.9542L16.3152 15.6401C16.092 15.9165 15.8203 16.1617 15.5088 16.3653L15.7824 16.7838L16.0561 17.2023C16.452 16.9434 16.8024 16.6286 17.0933 16.2683L16.7042 15.9542ZM15.7824 3.21619L15.5088 3.63466C15.8203 3.83835 16.092 4.08346 16.3152 4.35987L16.7042 4.04581L17.0933 3.73174C16.8024 3.37144 16.452 3.05662 16.0561 2.79771L15.7824 3.21619ZM10 2.5V2C8.44765 2 7.24721 1.99944 6.29919 2.09188C5.34458 2.18497 4.58824 2.3764 3.94392 2.79771L4.21756 3.21619L4.4912 3.63466C4.94215 3.33979 5.51464 3.17313 6.39624 3.08716C7.28443 3.00056 8.42757 3 10 3V2.5ZM2.5 9.25H3C3 7.8315 3.00084 6.81221 3.09589 6.0227C3.18938 5.24602 3.36841 4.75181 3.68481 4.35987L3.29576 4.04581L2.90671 3.73174C2.42735 4.32555 2.2085 5.02728 2.10305 5.90319C1.99916 6.76627 2 7.8562 2 9.25H2.5ZM4.21756 3.21619L3.94392 2.79771C3.54797 3.05662 3.19757 3.37144 2.90671 3.73174L3.29576 4.04581L3.68481 4.35987C3.90795 4.08346 4.1797 3.83835 4.4912 3.63466L4.21756 3.21619ZM10 2.5V3C10.9766 3 11.7903 3.00005 12.4846 3.02146L12.5 2.5217L12.5154 2.02193C11.8024 1.99995 10.9716 2 10 2V2.5ZM12.5 2.5217L12.4846 3.02146C14.0354 3.06928 14.8825 3.22514 15.5088 3.63466L15.7824 3.21619L16.0561 2.79771C15.1747 2.22138 14.0715 2.06992 12.5154 2.02193L12.5 2.5217ZM12.5 2.5217L12 2.5217C12 3.18374 11.9987 3.5812 12.0589 3.92329L12.5513 3.83663L13.0437 3.74997C13.0024 3.51515 13 3.2243 13 2.52169L12.5 2.5217ZM17.5 9.25H18C18 8.37638 18.0001 7.62578 17.9755 6.98098L17.4759 7L16.9763 7.01902C16.9999 7.64074 17 8.37023 17 9.25H17.5ZM17.4759 7L17.9755 6.98098C17.9219 5.57196 17.7512 4.54676 17.0933 3.73174L16.7042 4.04581L16.3152 4.35987C16.7526 4.90176 16.9234 5.63176 16.9763 7.01902L17.4759 7ZM17.4759 7L17.4759 6.5C16.7012 6.49999 16.3621 6.49828 16.0854 6.45884L16.0149 6.95383L15.9443 7.44883C16.3086 7.50076 16.7343 7.5 17.4759 7.5L17.4759 7ZM10 7.75H9.5V13.15H10H10.5V7.75H10ZM13.3333 11.35L12.9972 10.9798L11.8323 12.0376L12.1685 12.4078L12.5046 12.7779L13.6695 11.7202L13.3333 11.35ZM7.83154 12.4078L8.16767 12.0376L7.00279 10.9798L6.66667 11.35L6.33054 11.7202L7.49541 12.7779L7.83154 12.4078ZM12.1685 12.4078L11.8323 12.0376C11.3594 12.467 11.0348 12.7608 10.7597 12.9597C10.4941 13.1516 10.3176 13.2223 10.1519 13.2413L10.2089 13.7381L10.2659 14.2348C10.6663 14.1889 11.0051 14.0162 11.3455 13.7701C11.6764 13.531 12.0485 13.1921 12.5046 12.7779L12.1685 12.4078ZM7.83154 12.4078L7.49541 12.7779C7.95149 13.1921 8.32359 13.531 8.65449 13.7701C8.99492 14.0162 9.33371 14.1889 9.73413 14.2348L9.79111 13.7381L9.84809 13.2413C9.68239 13.2223 9.50587 13.1516 9.24028 12.9597C8.96517 12.7608 8.64055 12.467 8.16767 12.0376L7.83154 12.4078ZM10.2089 13.7381L10.1519 13.2413C10.0511 13.2529 9.94894 13.2529 9.84809 13.2413L9.79111 13.7381L9.73413 14.2348C9.9107 14.2551 10.0893 14.2551 10.2659 14.2348L10.2089 13.7381ZM10 13.15H9.5V13.7381H10H10.5V13.15H10Z"
                    fill="white"
                  />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.0149 6.95383L15.9443 7.44883L16.0149 6.95383ZM12.5513 3.83663L12.0589 3.92329V3.92329L12.5513 3.83663ZM4.21756 16.7838L4.4912 16.3653L4.21756 16.7838ZM3.29576 15.9542L3.68481 15.6401L3.29576 15.9542ZM16.7042 15.9542L16.3152 15.6401L16.7042 15.9542ZM15.7824 16.7838L15.5088 16.3653L15.7824 16.7838ZM15.7824 3.21619L16.0561 2.79771L15.7824 3.21619ZM16.7042 4.04581L17.0933 3.73174L16.7042 4.04581ZM4.21756 3.21619L4.4912 3.63466L4.21756 3.21619ZM3.29576 4.04581L3.68481 4.35987L3.29576 4.04581ZM12.5 2.5217L12.5154 2.02193L12.5 2.5217ZM17.4759 7L17.9755 6.98098L17.4759 7ZM10.5 7.75C10.5 7.47386 10.2761 7.25 10 7.25C9.72386 7.25 9.5 7.47386 9.5 7.75H10.5ZM13.6695 11.7202C13.8739 11.5345 13.8891 11.2183 13.7035 11.0139C13.5179 10.8094 13.2016 10.7942 12.9972 10.9798L13.6695 11.7202ZM12.1685 12.4078L12.5046 12.7779L12.1685 12.4078ZM7.83154 12.4078L8.16767 12.0376H8.16767L7.83154 12.4078ZM7.00279 10.9798C6.79836 10.7942 6.48215 10.8094 6.29651 11.0139C6.11087 11.2183 6.1261 11.5345 6.33054 11.7202L7.00279 10.9798ZM10.2089 13.7381L10.2659 14.2348H10.2659L10.2089 13.7381ZM9.79111 13.7381L9.73413 14.2348H9.73413L9.79111 13.7381ZM9.5 13.7381C9.5 14.0142 9.72386 14.2381 10 14.2381C10.2761 14.2381 10.5 14.0142 10.5 13.7381H9.5ZM16.0149 6.95383L16.0854 6.45884C14.4864 6.2309 13.2836 5.11284 13.0437 3.74997L12.5513 3.83663L12.0589 3.92329C12.3838 5.76961 13.9776 7.16848 15.9443 7.44883L16.0149 6.95383ZM17.5 9.25H17V10.75H17.5H18V9.25H17.5ZM2.5 10.75H3V9.25H2.5H2V10.75H2.5ZM10 17.5V17C8.42757 17 7.28443 16.9994 6.39624 16.9128C5.51464 16.8269 4.94215 16.6602 4.4912 16.3653L4.21756 16.7838L3.94392 17.2023C4.58824 17.6236 5.34458 17.815 6.29919 17.9081C7.24721 18.0006 8.44765 18 10 18V17.5ZM2.5 10.75H2C2 12.1438 1.99916 13.2337 2.10305 14.0968C2.2085 14.9727 2.42735 15.6745 2.90671 16.2683L3.29576 15.9542L3.68481 15.6401C3.36841 15.2482 3.18938 14.754 3.09589 13.9773C3.00084 13.1878 3 12.1685 3 10.75H2.5ZM4.21756 16.7838L4.4912 16.3653C4.1797 16.1617 3.90795 15.9165 3.68481 15.6401L3.29576 15.9542L2.90671 16.2683C3.19757 16.6286 3.54797 16.9434 3.94392 17.2023L4.21756 16.7838ZM17.5 10.75H17C17 12.1685 16.9992 13.1878 16.9041 13.9773C16.8106 14.754 16.6316 15.2482 16.3152 15.6401L16.7042 15.9542L17.0933 16.2683C17.5727 15.6745 17.7915 14.9727 17.8969 14.0968C18.0008 13.2337 18 12.1438 18 10.75H17.5ZM10 17.5V18C11.5523 18 12.7528 18.0006 13.7008 17.9081C14.6554 17.815 15.4118 17.6236 16.0561 17.2023L15.7824 16.7838L15.5088 16.3653C15.0578 16.6602 14.4854 16.8269 13.6038 16.9128C12.7156 16.9994 11.5724 17 10 17V17.5ZM16.7042 15.9542L16.3152 15.6401C16.092 15.9165 15.8203 16.1617 15.5088 16.3653L15.7824 16.7838L16.0561 17.2023C16.452 16.9434 16.8024 16.6286 17.0933 16.2683L16.7042 15.9542ZM15.7824 3.21619L15.5088 3.63466C15.8203 3.83835 16.092 4.08346 16.3152 4.35987L16.7042 4.04581L17.0933 3.73174C16.8024 3.37144 16.452 3.05662 16.0561 2.79771L15.7824 3.21619ZM10 2.5V2C8.44765 2 7.24721 1.99944 6.29919 2.09188C5.34458 2.18497 4.58824 2.3764 3.94392 2.79771L4.21756 3.21619L4.4912 3.63466C4.94215 3.33979 5.51464 3.17313 6.39624 3.08716C7.28443 3.00056 8.42757 3 10 3V2.5ZM2.5 9.25H3C3 7.8315 3.00084 6.81221 3.09589 6.0227C3.18938 5.24602 3.36841 4.75181 3.68481 4.35987L3.29576 4.04581L2.90671 3.73174C2.42735 4.32555 2.2085 5.02728 2.10305 5.90319C1.99916 6.76627 2 7.8562 2 9.25H2.5ZM4.21756 3.21619L3.94392 2.79771C3.54797 3.05662 3.19757 3.37144 2.90671 3.73174L3.29576 4.04581L3.68481 4.35987C3.90795 4.08346 4.1797 3.83835 4.4912 3.63466L4.21756 3.21619ZM10 2.5V3C10.9766 3 11.7903 3.00005 12.4846 3.02146L12.5 2.5217L12.5154 2.02193C11.8024 1.99995 10.9716 2 10 2V2.5ZM12.5 2.5217L12.4846 3.02146C14.0354 3.06928 14.8825 3.22514 15.5088 3.63466L15.7824 3.21619L16.0561 2.79771C15.1747 2.22138 14.0715 2.06992 12.5154 2.02193L12.5 2.5217ZM12.5 2.5217L12 2.5217C12 3.18374 11.9987 3.5812 12.0589 3.92329L12.5513 3.83663L13.0437 3.74997C13.0024 3.51515 13 3.2243 13 2.52169L12.5 2.5217ZM17.5 9.25H18C18 8.37638 18.0001 7.62578 17.9755 6.98098L17.4759 7L16.9763 7.01902C16.9999 7.64074 17 8.37023 17 9.25H17.5ZM17.4759 7L17.9755 6.98098C17.9219 5.57196 17.7512 4.54676 17.0933 3.73174L16.7042 4.04581L16.3152 4.35987C16.7526 4.90176 16.9234 5.63176 16.9763 7.01902L17.4759 7ZM17.4759 7L17.4759 6.5C16.7012 6.49999 16.3621 6.49828 16.0854 6.45884L16.0149 6.95383L15.9443 7.44883C16.3086 7.50076 16.7343 7.5 17.4759 7.5L17.4759 7ZM10 7.75H9.5V13.15H10H10.5V7.75H10ZM13.3333 11.35L12.9972 10.9798L11.8323 12.0376L12.1685 12.4078L12.5046 12.7779L13.6695 11.7202L13.3333 11.35ZM7.83154 12.4078L8.16767 12.0376L7.00279 10.9798L6.66667 11.35L6.33054 11.7202L7.49541 12.7779L7.83154 12.4078ZM12.1685 12.4078L11.8323 12.0376C11.3594 12.467 11.0348 12.7608 10.7597 12.9597C10.4941 13.1516 10.3176 13.2223 10.1519 13.2413L10.2089 13.7381L10.2659 14.2348C10.6663 14.1889 11.0051 14.0162 11.3455 13.7701C11.6764 13.531 12.0485 13.1921 12.5046 12.7779L12.1685 12.4078ZM7.83154 12.4078L7.49541 12.7779C7.95149 13.1921 8.32359 13.531 8.65449 13.7701C8.99492 14.0162 9.33371 14.1889 9.73413 14.2348L9.79111 13.7381L9.84809 13.2413C9.68239 13.2223 9.50587 13.1516 9.24028 12.9597C8.96517 12.7608 8.64055 12.467 8.16767 12.0376L7.83154 12.4078ZM10.2089 13.7381L10.1519 13.2413C10.0511 13.2529 9.94894 13.2529 9.84809 13.2413L9.79111 13.7381L9.73413 14.2348C9.9107 14.2551 10.0893 14.2551 10.2659 14.2348L10.2089 13.7381ZM10 13.15H9.5V13.7381H10H10.5V13.15H10Z"
                    fill="#2662D9"
                  />
                </svg>
              )}
            </Button>
          </div>
        </div>
      </div>
      <div className="fuel-levels">
        {sortedTanks.filter((tank) => selectedTanks.includes(tank.vehicle_name))
          .length > 0 ? (
          sortedTanks
            .filter((tank) => selectedTanks.includes(tank.vehicle_name))
            .map((tank) => (
              <FuelLevel
                key={tank.vehicle_name}
                tankLabel={tank.vehicle_name}
                currentLevel={tank.current_fuel}
                totalCapacity={tank.total_capacity}
              />
            ))
        ) : (
          <div className="empty-tag">
            <Empty
              description={
                <span style={{ color: "#FB3748" }}>
                  Select Vechicle to Display Tanks
                </span>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentFuelLevels;
